/**
 * Показ пользовательского/ИИ HTML вне origin приложения: iframe с sandbox="allow-scripts"
 * (без allow-same-origin) получает opaque origin и не видит токены, cookies и DOM приложения.
 */

export const PREVIEW_SANDBOX = 'allow-scripts';

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
};

export const escapeHtml = (value: string): string => value.replace(/[&<>"']/g, ch => HTML_ESCAPES[ch]);

/** CSS для вставки внутрь <style>: не даёт закрыть тег раньше времени. */
export const escapeStyleText = (css: string): string => css.replace(/<\/(style)/gi, '<\\/$1');

/** JSON для вставки внутрь <script>. */
export const jsonForInlineScript = (value: unknown): string =>
  JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');

/** Ресурсы (/backgrounds/, /assets/, /generated-assets/) грузятся с origin приложения. */
export const getAppBaseHref = (): string => `${window.location.origin}/`;

export const buildBaseTag = (baseHref: string = getAppBaseHref()): string => `<base href="${escapeHtml(baseHref)}">`;

/** Добавляет <base href> в полный HTML-документ (если своего <base> нет). */
export const withBaseHref = (html: string, baseHref: string = getAppBaseHref()): string => {
  if (/<base[\s>]/i.test(html)) return html;
  const baseTag = buildBaseTag(baseHref);
  if (/<head(\s[^>]*)?>/i.test(html)) {
    return html.replace(/<head(\s[^>]*)?>/i, match => `${match}${baseTag}`);
  }
  if (/<html(\s[^>]*)?>/i.test(html)) {
    return html.replace(/<html(\s[^>]*)?>/i, match => `${match}<head>${baseTag}</head>`);
  }
  return `<!DOCTYPE html><html><head><meta charset="UTF-8">${baseTag}</head><body>${html}</body></html>`;
};

// Статичная оболочка окна: без пользовательских данных. Сам HTML попадает только в srcdoc
// sandboxed iframe через DOM-свойство, поэтому на origin приложения он не исполняется.
const WINDOW_SHELL = '<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>html,body{margin:0;height:100%;background:#fff;font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#444}iframe{display:block;width:100%;height:100%;border:0}.status{padding:24px}</style></head><body><p class="status" id="sandboxed-preview-status"></p></body></html>';

export interface SandboxedPreviewWindow {
  render: (html: string) => void;
  close: () => void;
}

/**
 * Открывает новое окно синхронно (в обработчике клика), обнуляет opener и показывает
 * переданный HTML внутри iframe sandbox="allow-scripts" через srcdoc.
 */
export const openSandboxedPreviewWindow = (
  title: string,
  loadingText = 'Загрузка превью…'
): SandboxedPreviewWindow | null => {
  const previewWindow = window.open('', '_blank');
  if (!previewWindow) return null;
  try {
    previewWindow.opener = null;
  } catch {
    // окно открыто, opener просто не удалось сбросить
  }

  const doc = previewWindow.document;
  doc.open();
  doc.write(WINDOW_SHELL);
  doc.close();
  doc.title = title;
  const status = doc.getElementById('sandboxed-preview-status');
  if (status) status.textContent = loadingText;

  return {
    render: (html: string) => {
      const frame = doc.createElement('iframe');
      frame.setAttribute('sandbox', PREVIEW_SANDBOX);
      frame.setAttribute('title', title);
      frame.srcdoc = withBaseHref(html);
      doc.body.textContent = '';
      doc.body.appendChild(frame);
    },
    close: () => {
      try {
        previewWindow.close();
      } catch {
        // окно уже закрыто
      }
    }
  };
};
